<!--
  Customer Pricing dashboard (Story 3.1, design-spec §3b–§3e). Renders inside the existing app
  shell (sidebar + topbar + theme). `%` only, never a `$` (BR-07). Write actions + bulk toolbar (3.2–3.5),
  threshold config + suppliers directory (3.6–3.7), and the supplier filter pill bar (Story 4.2) are now
  live; group-by/chips/pp/date filters still to come (Stories 4.3–4.6).
-->
<section class="pf-content" aria-labelledby="pf-customer-pricing-heading">
  <!-- Header row: title + a gear link to the Customer Portal Settings (outlier threshold, Story 3.6,
       design §5.5). The pricing area is URL-only, so the gear lives in the page header (its "topbar"). -->
  <div class="pf-page-header">
    <h1 id="pf-customer-pricing-heading">Customer Pricing</h1>
    <!-- Tab bar (§3b) — sits inline in the page header, between the title and the Suppliers/Settings
         actions, to save vertical space. -->
    <dx-tabs
      class="pf-tabs"
      [items]="tabItems"
      [selectedIndex]="selectedIndex"
      [showNavButtons]="false"
      (onItemClick)="onTabClick($event)"
      aria-label="Discount review tabs"
    ></dx-tabs>
    <div class="pf-page-actions">
      <!-- Connected Suppliers directory (Story 3.7). The pricing area is URL-only, so the link
           lives in the page header (its "topbar") beside the settings gear. -->
      <a
        class="pf-settings-gear"
        routerLink="/customer-pricing/suppliers"
        aria-label="Open the connected suppliers directory"
        title="Suppliers"
      >
        <svg viewBox="0 0 20 20" aria-hidden="true" width="20" height="20">
          <path
            d="M7 9a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M13 9a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M2.5 16v-1.5a3.5 3.5 0 013.5-3.5h2a3.5 3.5 0 013.5 3.5V16 M13 11h1a3.5 3.5 0 013.5 3.5V16"
            fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round"
          />
        </svg>
        <span>Suppliers</span>
      </a>
      <a
        class="pf-settings-gear"
        routerLink="/customer-pricing/settings"
        aria-label="Open settings — outlier threshold"
        title="Settings"
      >
        <svg viewBox="0 0 20 20" aria-hidden="true" width="20" height="20">
          <path
            d="M10 6.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z M10 1.5l1 2.2 2.4-.4 1 2.1 2 1.4-.7 2.3.7 2.3-2 1.4-1 2.1-2.4-.4-1 2.2-1-2.2-2.4.4-1-2.1-2-1.4.7-2.3-.7-2.3 2-1.4 1-2.1 2.4.4 1-2.2z"
            fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"
          />
        </svg>
        <span>Settings</span>
      </a>
    </div>
  </div>
  <p class="pf-page-description">Review and action supplier discount changes across pending, update and accepted tabs.</p>
  <p class="pf-page-description">Dollar discounts use the same cost basis as the promotion: current ex-GST/ex-WET pricing for each review’s account, so they move with later price changes.</p>

  <!-- ============================ Summary cards (§3b) ============================ -->
  <!-- A failed counts fetch shows '—' (not a misleading 0) plus this alert, so an error is not mistaken for an empty account. -->
  <p *ngIf="countsError" class="pf-empty" role="alert">Couldn't load the dashboard summary. Try refreshing.</p>
  <div class="pf-summary-cards" aria-label="Discount verification summary">
    <div class="pf-card">
      <div class="pf-card-label">PRODUCTS PENDING REVIEW</div>
      <div class="pf-card-value pf-card-value--accent">{{ countsLoading || countsError ? '—' : (counts?.pending_review ?? 0) }}</div>
      <div class="pf-card-sub">discounts awaiting supplier response</div>
    </div>

    <div class="pf-card">
      <div class="pf-card-label">UPDATE REQUESTS</div>
      <div class="pf-card-value pf-card-value--amber">{{ countsLoading || countsError ? '—' : (counts?.update_requests ?? 0) }}</div>
      <div class="pf-card-sub">supplier requests to review</div>
      <!-- Outlier sub-line — renders only once a customer threshold flags ≥ 1 row (Story 3.6 sets the threshold; AC2). -->
      <div class="pf-card-outliers" *ngIf="outlierSubCount > 0">
        <svg class="pf-outlier-icon" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 1L15 14H1L8 1z M8 6v4 M8 11.5v.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
        </svg>
        {{ outlierSubCount }} flagged as outliers
      </div>
      <!-- "Configure threshold" deep-link to the Settings page (Story 3.6, §3g Access). -->
      <a class="pf-card-link" routerLink="/customer-pricing/settings" aria-label="Configure the outlier threshold">
        Configure threshold
      </a>
    </div>

    <div class="pf-card">
      <div class="pf-card-label">ACCEPTED THIS MONTH</div>
      <div class="pf-card-value pf-card-value--green">{{ countsLoading || countsError ? '—' : (counts?.accepted_this_month ?? 0) }}</div>
      <div class="pf-card-sub">discount changes confirmed</div>
    </div>

    <div class="pf-card">
      <div class="pf-card-label">SUPPLIERS</div>
      <div class="pf-card-value">{{ countsLoading || countsError ? '—' : (counts?.suppliers_with_active_reviews ?? 0) }}</div>
      <div class="pf-card-sub">with active discount reviews</div>
    </div>
  </div>

  <!-- Supplier filter (Story 4.2) and Group-by (4.3) controls live on each grid's native toolbar — see the
       shared #supplierFilterTpl / #groupByTpl templates below (defined once, outletted into every grid's
       dxo-toolbar). The Story 4.4 status chips + Story 4.5 Discount Delta filter were retired in favour of
       native column header filters (funnel icons) on the Status + Change columns. -->

  <!-- Tab bar (§3b) moved up into the page header (between the title and the Suppliers/Settings actions). -->

  <!-- ============================ Accepted date filter bar (§3e, Story 4.6) ============================ -->
  <!-- TAB-LOCAL (BR-40): rendered ONLY on the Accepted tab, never Pending Review / Update Requests. Five
       BR-13 presets + a custom From/To range, filtering the loaded Accepted rows client-side by their
       immutable effective_date (Accepted Date). Composes by AND with the 4.2 supplier filter, 4.3 group-by
       and the native Change/Status header filters (the grid applies those). Native <button> pills give Tab focus +
       Enter/Space for free; aria-pressed conveys selection beyond colour (AC10). DevExtreme dx-date-box for
       the From/To pickers (in-app Material theme). No `$` anywhere (BR-07). -->
  <div
    *ngIf="showDateBar"
    class="pf-filter-bar pf-date-bar"
    role="group"
    aria-label="Filter accepted discounts by date"
  >
    <span class="pf-delta-label">Accepted:</span>
    <button
      type="button"
      class="pf-chip pf-focusable"
      [class.pf-chip--active]="isDateAllSelected"
      [attr.aria-pressed]="isDateAllSelected"
      (click)="clearDateFilter()"
    >
      All
    </button>
    <button
      *ngFor="let p of datePresetItems"
      type="button"
      class="pf-chip pf-focusable"
      [class.pf-chip--active]="isDatePresetSelected(p.key)"
      [attr.aria-pressed]="isDatePresetSelected(p.key)"
      (click)="selectDatePreset(p.key)"
    >
      {{ p.label }}
    </button>

    <!-- Custom range From/To pickers + Apply — revealed only while the "Custom range" preset is active (AC3). -->
    <ng-container *ngIf="isDatePresetSelected('custom')">
      <span class="pf-date-fields">
        <dx-date-box
          type="date"
          displayFormat="dd MMM yyyy"
          [value]="customFrom"
          (onValueChanged)="customFrom = $event.value"
          [inputAttr]="{ 'aria-label': 'Accepted from date' }"
          [width]="150"
        ></dx-date-box>
        <span class="pf-delta-dash" aria-hidden="true">–</span>
        <dx-date-box
          type="date"
          displayFormat="dd MMM yyyy"
          [value]="customTo"
          (onValueChanged)="customTo = $event.value"
          [inputAttr]="{ 'aria-label': 'Accepted to date' }"
          [width]="150"
        ></dx-date-box>
        <button type="button" class="pf-delta-apply pf-focusable" (click)="applyCustomRange()">Apply</button>
      </span>
      <span class="pf-delta-hint" role="alert" *ngIf="dateRangeHint">{{ dateRangeHint }}</span>
    </ng-container>

    <!-- Active-summary pill + clear (shown for a custom range so the chosen window + a clear control are
         always visible; preset selection is already conveyed by the active pill, AC3a). -->
    <span class="pf-delta-pill" *ngIf="isDatePresetSelected('custom') && dateFilterSummary">
      {{ dateFilterSummary }}
      <button
        type="button"
        class="pf-delta-pill-clear pf-focusable"
        (click)="clearDateFilter()"
        aria-label="Clear date filter"
      >
        ×
      </button>
    </span>
  </div>

  <!-- ============================ Shared grid-toolbar control templates ============================ -->
  <!-- The supplier lookup (Story 4.2) and group-by (4.3) controls live on each grid's native dxo-toolbar
       (alongside the search box) to save vertical space. Defined ONCE here and outletted into every grid's
       toolbar via ngTemplateOutlet, so the markup + bound state stay single-sourced (only the visible tab's
       grid is in the DOM at a time). The Story 4.4 status chips + Story 4.5 Discount Delta controls that
       used to sit here were retired in favour of native column header filters (see the Status + Change
       columns). NOTE: a grid's toolbar only renders while the grid itself is shown — on the loading / error
       / empty states the grid is [hidden], so these controls hide with it. -->
  <ng-template #supplierFilterTpl>
    <div class="pf-toolbar-control" role="group" aria-label="Filter by supplier">
      <span class="pf-supplier-label" id="pf-supplier-label">Supplier</span>
      <dx-select-box
        class="pf-supplier-select"
        [dataSource]="supplierPills"
        valueExpr="supplier_code"
        displayExpr="supplier_name"
        [value]="selectedSupplierCode"
        [searchEnabled]="true"
        searchMode="contains"
        searchExpr="supplier_name"
        [showClearButton]="true"
        placeholder="All suppliers"
        [width]="220"
        (onValueChanged)="selectSupplier($event.value ?? null)"
        [inputAttr]="{ 'aria-labelledby': 'pf-supplier-label' }"
      ></dx-select-box>
    </div>
  </ng-template>

  <ng-template #groupByTpl>
    <div class="pf-toolbar-control">
      <span class="pf-groupby-label" id="pf-groupby-label">Group by</span>
      <dx-button-group
        class="pf-groupby"
        [items]="$any(groupByItems)"
        keyExpr="key"
        [selectedItemKeys]="[groupBy]"
        selectionMode="single"
        stylingMode="outlined"
        (onItemClick)="onGroupByChange($event)"
        [elementAttr]="{ 'aria-labelledby': 'pf-groupby-label' }"
      ></dx-button-group>
    </div>
  </ng-template>

  <!-- ============================ Tab content ============================ -->
  <ng-container [ngSwitch]="currentTab">

    <!-- Skeleton-row loading state (shared pattern) -->
    <div *ngIf="currentState.loading" class="pf-skeleton" aria-busy="true" aria-label="Loading">
      <div class="pf-skeleton-row" *ngFor="let r of skeletonRows"></div>
    </div>

    <!-- Load error -->
    <p *ngIf="!currentState.loading && currentState.error" class="pf-empty" role="alert">
      The list could not be loaded. Please reload the page and try again.
    </p>

    <!-- No separate empty panel for the loaded-empty case: the grid stays rendered and shows its own
         no-data text (gridNoDataText) so its toolbar — supplier / group-by / delta / search — remains
         reachable when a filter narrows the list to zero. The loading + error states above still apply. -->

    <!-- ---------- Pending Review tab (§3c) ---------- -->
    <div
      *ngSwitchCase="'pending'"
      [hidden]="currentState.loading || currentState.error"
      class="pf-table-wrap"
    >
      <!-- Sticky bulk action toolbar (§3c) — shows once ≥ 1 row is selected (AC6). -->
      <div class="pf-bulk-bar" *ngIf="selectedCount > 0" role="region" aria-label="Bulk actions">
        <span class="pf-bulk-count">{{ selectedCount }} selected</span>
        <dx-button
          text="Bulk Accept"
          type="success"
          stylingMode="contained"
          (onClick)="openBulkAccept()"
          aria-label="Bulk accept the selected products"
        ></dx-button>
        <dx-button
          text="Bulk Update"
          stylingMode="outlined"
          [disabled]="!bulkUpdateEnabled"
          [hint]="bulkUpdateDisabledHint"
          (onClick)="openBulkUpdate()"
          [attr.aria-label]="bulkUpdateDisabledHint"
        ></dx-button>
      </div>

      <dx-data-grid
        #pendingGrid
        [dataSource]="displayRows.pending"
        keyExpr="id"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [wordWrapEnabled]="true"
        height="100%"
        [noDataText]="gridNoDataText(currentTab)"
        [customizeColumns]="hideExpandColumn"
        (onSelectionChanged)="onPendingSelectionChanged($event)"
        (onRowPrepared)="onPendingRowPrepared($event)"
        (onRowExpanded)="onRowExpanded('pending', $event)"
        (onRowCollapsed)="onRowCollapsed('pending', $event)"
        (onContentReady)="onGridContentReady('pending', $event)"
        aria-label="Products pending supplier review"
      >
        <!-- Bulk-select: row checkboxes + per-group header checkbox + top "Select all" (AC6). -->
        <dxo-selection mode="multiple" showCheckBoxesMode="always" [allowSelectAll]="true"></dxo-selection>
        <!-- Inline Update form opened from the row "Update" button (expandRow); the per-row expand
             arrow is hidden via customizeColumns (AC4). -->
        <dxo-master-detail [enabled]="true" template="updateForm"></dxo-master-detail>
        <!-- Search field in the grid toolbar — client-side over the loaded rows; matches product
             name/code (via calculateCellValue), catalogue, and supplier. Composes with the chip /
             supplier / delta filters above the grid. -->
        <dxo-search-panel [visible]="true" [width]="240" placeholder="Search products…"></dxo-search-panel>
        <!-- Native column header filters (funnel icons) — replace the retired Story 4.4 status chips +
             Story 4.5 Discount Delta toolbar controls. Wired on the Status + Change columns below. -->
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <!-- Status chips (4.4) + Supplier (4.2) / Group-by (4.3) / Delta (4.5) filters on the grid's own
             toolbar, beside the search box — outlet the shared templates; [visible] mirrors each control's
             original show rule. -->
        <dxo-toolbar>
          <dxi-item location="before" [visible]="showSupplierBar">
            <div *dxTemplate><ng-container [ngTemplateOutlet]="supplierFilterTpl"></ng-container></div>
          </dxi-item>
          <dxi-item location="before" [visible]="showGroupByBar">
            <div *dxTemplate><ng-container [ngTemplateOutlet]="groupByTpl"></ng-container></div>
          </dxi-item>
          <dxi-item name="searchPanel" location="after"></dxi-item>
        </dxo-toolbar>
        <dxo-grouping [autoExpandAll]="true"></dxo-grouping>
        <!-- No pager — virtual scrolling lets the user scroll the full result set in one list while
             keeping render fast at 3K+ rows (mirrors the supplier portal grids). -->
        <dxo-paging [pageSize]="50"></dxo-paging>
        <dxo-scrolling mode="virtual" rowRenderingMode="virtual"></dxo-scrolling>
        <dxi-column dataField="catalogue" caption="Catalogue" cssClass="pf-col-catalogue"
          [groupIndex]="catalogueGroupIndex" groupCellTemplate="groupHeader"></dxi-column>
        <dxi-column dataField="promotion_name" dataType="string" caption="Promotion" [minWidth]="180"
          [allowFiltering]="true" [allowHeaderFiltering]="true" [allowGrouping]="true"
          [showWhenGrouped]="true" [groupIndex]="promotionGroupIndex" groupCellTemplate="groupHeader"></dxi-column>

        <dxi-column caption="Product" dataField="product_name" cssClass="pf-col-product" cellTemplate="productCell"
          [allowSorting]="false" [calculateCellValue]="productSearchValue"></dxi-column>
        <dxi-column dataField="supplier_name" caption="Supplier" cssClass="pf-col-supplier" cellTemplate="supplierCell"
          [allowSorting]="false" [groupIndex]="supplierGroupIndex" groupCellTemplate="groupHeader"></dxi-column>
        <dxi-column caption="Discount (%)" cssClass="pf-col-discount" cellTemplate="currentCell" [allowSorting]="false"></dxi-column>
        <dxi-column caption="Discount ($)" dataType="number" cellTemplate="amountCell"
          [calculateCellValue]="currentDiscountAmount" [allowFiltering]="false"></dxi-column>
        <dxi-column caption="Previous (%)" cssClass="pf-col-discount" cellTemplate="previousCell" [allowSorting]="false"></dxi-column>
        <dxi-column caption="Previous ($)" dataType="number" cellTemplate="amountCell"
          [calculateCellValue]="previousDiscountAmount" [allowFiltering]="false"></dxi-column>
        <div *dxTemplate="let cell of 'amountCell'">
          {{ cell.value == null ? '—' : (cell.value | currency:'AUD':'symbol-narrow':'1.2-2') }}
        </div>
        <dxi-column caption="Change" cssClass="pf-col-delta" cellTemplate="deltaCell" [allowSorting]="false"
          [calculateCellValue]="deltaBandValue" [calculateFilterExpression]="deltaFilterExpression"
          [headerFilter]="deltaHeaderFilter" [allowSearch]="false"></dxi-column>
        <dxi-column caption="Status" cellTemplate="statusCell" [allowSorting]="false"
          [calculateCellValue]="statusFilterValue" [calculateFilterExpression]="statusFilterExpression"
          [headerFilter]="statusHeaderFilter" [allowSearch]="false"></dxi-column>
        <dxi-column caption="Actions" cssClass="pf-col-actions-wide" cellTemplate="actionsCell" [allowSorting]="false" [allowFiltering]="false"></dxi-column>

        <!-- Shared group-header (§4.9, Story 4.3): label per mode + collapsed pp-delta avg (visible when
             collapsed). Rendered by whichever column is grouped (catalogue or supplier). No `$` (BR-07). -->
        <div *dxTemplate="let group of 'groupHeader'" class="pf-group-header" [attr.aria-label]="groupAriaLabel(group)">
          <span class="pf-group-label">{{ groupHeaderLabel(group) }}</span>
          <span class="pf-group-summary" [ngClass]="groupAvgDeltaClass(group)">{{ groupAvgDeltaText(group) }}</span>
        </div>

        <div *dxTemplate="let cell of 'productCell'">
          <div class="pf-product-name">{{ cell.data.product_name || cell.data.product_code }}</div>
          <div class="pf-product-code">
            {{ cell.data.product_code }}<span *ngIf="cell.data.units_per_case"> · {{ cell.data.units_per_case }}/case</span>
          </div>
        </div>
        <div *dxTemplate="let cell of 'supplierCell'">
          <div>{{ cell.data.supplier_name }}</div>
          <div class="pf-product-code" *ngIf="cell.data.date_added">{{ cell.data.date_added | date:'d MMM y' }}</div>
        </div>
        <div *dxTemplate="let cell of 'currentCell'">
          <app-discount-value-cell
            [tier1Value]="cell.data.current_discount_percent"
            [tier1Name]="cell.data.discount_name"
            valueContext="Current"
            [precision]="1"
          ></app-discount-value-cell>
        </div>
        <div *dxTemplate="let cell of 'previousCell'">
          <app-discount-value-cell
            [tier1Value]="cell.data.previous_discount_percent"
            [tier1Name]="cell.data.discount_name"
            valueContext="Previous"
            [precision]="1"
          ></app-discount-value-cell>
        </div>
        <div *dxTemplate="let cell of 'deltaCell'">
          <app-discount-delta-cell
            [deltaPp]="cell.data.delta_pp"
            [tierName]="cell.data.discount_name"
            [breached]="cell.data.breached"
          ></app-discount-delta-cell>
        </div>
        <div *dxTemplate="let cell of 'statusCell'">
          <app-status-badge [status]="cell.data.status"></app-status-badge>
          <app-outlier-badge *ngIf="cell.data.is_outlier"></app-outlier-badge>
          <!-- "Reminder Sent" sub-badge (Story 3.5, §3c) — amber pill + clock icon below the status
               badge, driven by the row's reminder_sent_at. Theme/status tokens, not prototype hex. -->
          <div class="pf-reminder-badge-row" *ngIf="cell.data.reminder_sent_at">
            <span
              class="pf-reminder-badge"
              [title]="'Reminder sent ' + (cell.data.reminder_sent_at | date:'d MMM y, h:mm a')"
            >
              <svg class="pf-reminder-icon" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              Reminder Sent
            </span>
          </div>
        </div>

        <!-- Actions column: Accept (green) + Update (secondary) + Send/Resend Reminder (accent text
             link, Story 3.5 — single click, no confirmation dialog). -->
        <div *dxTemplate="let cell of 'actionsCell'">
          <div class="pf-actions">
            <dx-button
              text="Accept"
              type="success"
              stylingMode="contained"
              [disabled]="cell.data.price_data_missing"
              (onClick)="accept(cell.data)"
              [attr.aria-label]="'Accept discounts for ' + (cell.data.product_name || cell.data.product_code)"
            ></dx-button>
            <dx-button
              text="Update"
              stylingMode="outlined"
              [disabled]="cell.data.price_data_missing"
              (onClick)="openUpdate(cell.data)"
              [attr.aria-label]="'Update discounts for ' + (cell.data.product_name || cell.data.product_code)"
            ></dx-button>
            <dx-button
              class="pf-reminder-link"
              [text]="cell.data.reminder_sent_at ? 'Resend Reminder' : 'Send Reminder'"
              stylingMode="text"
              type="default"
              [disabled]="remindingIds.has(cell.data.id)"
              (onClick)="remind(cell.data)"
              [attr.aria-label]="(cell.data.reminder_sent_at ? 'Resend a reminder to ' : 'Send a reminder to ') + (cell.data.supplier_name || 'the supplier') + ' for ' + (cell.data.product_name || cell.data.product_code)"
            ></dx-button>
          </div>
          <!-- Inline error for a row a bulk action could not act on (AC9 — stays in place, flagged). -->
          <div class="pf-action-error" *ngIf="isFailed(cell.data.id)" role="alert">
            Action failed — still pending review
          </div>
        </div>

        <!-- Inline Update form (master-detail) — one discount input per configured tier + live
             pp helper, no $, no note (AC4). Rendered only for the row whose Update button opened it. -->
        <div *dxTemplate="let detail of 'updateForm'">
          <div class="pf-update-form" *ngIf="updateRow && updateRow.id === detail.data.id">
            <div class="pf-update-fields">
              <div class="pf-update-field">
                <label class="pf-update-label">Updated discount (%)</label>
                <dx-number-box
                  [value]="$any(updateForm.value)"
                  [showSpinButtons]="false"
                  format="#0.##"
                  [inputAttr]="{ 'aria-label': 'Updated discount percent' }"
                  (onValueChanged)="onUpdateValueChanged($event)"
                ></dx-number-box>
                <div class="pf-update-helper" [ngClass]="deltaClass(detail.data.current_discount_percent, updateForm.value)" aria-live="polite">
                  {{ deltaHelper(detail.data.current_discount_percent, updateForm.value) }}
                </div>
                <div class="pf-field-error" role="alert" *ngIf="updateForm.error">{{ updateForm.error }}</div>
              </div>
            </div>
            <div class="pf-update-actions">
              <dx-button text="Send update" type="default" [disabled]="updateForm.submitting" (onClick)="submitUpdate()"></dx-button>
              <dx-button text="Cancel" stylingMode="text" [disabled]="updateForm.submitting" (onClick)="cancelUpdate()"></dx-button>
            </div>
          </div>
        </div>
      </dx-data-grid>
    </div>

    <!-- ---------- Update Requests tab (§3d) ---------- -->
    <div
      *ngSwitchCase="'update-requests'"
      [hidden]="currentState.loading || currentState.error"
      class="pf-table-wrap"
    >
      <dx-data-grid
        #updateRequestsGrid
        [dataSource]="displayRows['update-requests']"
        keyExpr="id"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [wordWrapEnabled]="true"
        height="100%"
        [noDataText]="gridNoDataText(currentTab)"
        [customizeColumns]="hideExpandColumn"
        (onRowPrepared)="onUpdateRequestRowPrepared($event)"
        (onRowExpanded)="onRowExpanded('update-requests', $event)"
        (onRowCollapsed)="onRowCollapsed('update-requests', $event)"
        (onContentReady)="onGridContentReady('update-requests', $event)"
        aria-label="Supplier update requests"
      >
        <!-- Inline counter form opened from the row "Update" button (expandRow); the per-row expand
             arrow is hidden via customizeColumns (Story 3.4, AC1/AC2). -->
        <dxo-master-detail [enabled]="true" template="counterForm"></dxo-master-detail>
        <!-- Search field in the grid toolbar — see Pending grid note. -->
        <dxo-search-panel [visible]="true" [width]="240" placeholder="Search products…"></dxo-search-panel>
        <!-- Native column header filters (funnel icons) — replace the retired Story 4.4 status chips +
             Story 4.5 Discount Delta toolbar controls. Wired on the Status + Change columns below. -->
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <!-- Status chips (4.4) + Supplier (4.2) / Group-by (4.3) / Delta (4.5) filters on the grid's own
             toolbar, beside the search box — outlet the shared templates; [visible] mirrors each control's
             original show rule. -->
        <dxo-toolbar>
          <dxi-item location="before" [visible]="showSupplierBar">
            <div *dxTemplate><ng-container [ngTemplateOutlet]="supplierFilterTpl"></ng-container></div>
          </dxi-item>
          <dxi-item location="before" [visible]="showGroupByBar">
            <div *dxTemplate><ng-container [ngTemplateOutlet]="groupByTpl"></ng-container></div>
          </dxi-item>
          <dxi-item name="searchPanel" location="after"></dxi-item>
        </dxo-toolbar>
        <dxo-grouping [autoExpandAll]="true"></dxo-grouping>
        <!-- No pager — virtual scrolling lets the user scroll the full result set in one list while
             keeping render fast at 3K+ rows (mirrors the supplier portal grids). -->
        <dxo-paging [pageSize]="50"></dxo-paging>
        <dxo-scrolling mode="virtual" rowRenderingMode="virtual"></dxo-scrolling>
        <dxi-column dataField="catalogue" caption="Catalogue" cssClass="pf-col-catalogue"
          [groupIndex]="catalogueGroupIndex" groupCellTemplate="groupHeader"></dxi-column>
        <dxi-column dataField="promotion_name" dataType="string" caption="Promotion" [minWidth]="180"
          [allowFiltering]="true" [allowHeaderFiltering]="true" [allowGrouping]="true"
          [showWhenGrouped]="true" [groupIndex]="promotionGroupIndex" groupCellTemplate="groupHeader"></dxi-column>

        <dxi-column caption="Product" dataField="product_name" cssClass="pf-col-product" cellTemplate="productCell"
          [allowSorting]="false" [calculateCellValue]="productSearchValue"></dxi-column>
        <dxi-column dataField="supplier_name" caption="Supplier" cssClass="pf-col-supplier" cellTemplate="supplierCell"
          [allowSorting]="false" [groupIndex]="supplierGroupIndex" groupCellTemplate="groupHeader"></dxi-column>
        <dxi-column caption="Current (%)" cssClass="pf-col-discount" cellTemplate="currentCell" [allowSorting]="false"></dxi-column>
        <dxi-column caption="Current ($)" dataType="number" cellTemplate="amountCell"
          [calculateCellValue]="currentDiscountAmount" [allowFiltering]="false"></dxi-column>
        <dxi-column caption="Requested (%)" cssClass="pf-col-discount" cellTemplate="requestedCell" [allowSorting]="false"></dxi-column>
        <dxi-column caption="Requested ($)" dataType="number" cellTemplate="amountCell"
          [calculateCellValue]="requestedDiscountAmount" [allowFiltering]="false"></dxi-column>
        <div *dxTemplate="let cell of 'amountCell'">
          {{ cell.value == null ? '—' : (cell.value | currency:'AUD':'symbol-narrow':'1.2-2') }}
        </div>
        <dxi-column caption="Change" cssClass="pf-col-delta" cellTemplate="requestedDeltaCell" [allowSorting]="false"
          [calculateCellValue]="requestedDeltaBandValue" [calculateFilterExpression]="requestedDeltaFilterExpression"
          [headerFilter]="deltaHeaderFilter" [allowSearch]="false"></dxi-column>
        <dxi-column caption="Status" cellTemplate="statusCell" [allowSorting]="false"
          [calculateCellValue]="statusFilterValue" [calculateFilterExpression]="statusFilterExpression"
          [headerFilter]="statusHeaderFilter" [allowSearch]="false"></dxi-column>
        <dxi-column caption="Actions" cssClass="pf-col-actions" cellTemplate="actionsCell" [allowSorting]="false" [allowFiltering]="false"></dxi-column>

        <!-- Shared group-header (§4.9, Story 4.3) — see Pending grid note. -->
        <div *dxTemplate="let group of 'groupHeader'" class="pf-group-header" [attr.aria-label]="groupAriaLabel(group)">
          <span class="pf-group-label">{{ groupHeaderLabel(group) }}</span>
          <span class="pf-group-summary" [ngClass]="groupAvgDeltaClass(group)">{{ groupAvgDeltaText(group) }}</span>
        </div>

        <div *dxTemplate="let cell of 'productCell'">
          <div class="pf-product-name">{{ cell.data.product_name || cell.data.product_code }}</div>
          <div class="pf-product-code">
            {{ cell.data.product_code }}<span *ngIf="cell.data.units_per_case"> · {{ cell.data.units_per_case }}/case</span>
          </div>
        </div>
        <div *dxTemplate="let cell of 'supplierCell'">
          <div>{{ cell.data.supplier_name }}</div>
          <div class="pf-product-code" *ngIf="cell.data.date_added">Requested {{ cell.data.date_added | date:'d MMM y' }}</div>
        </div>
        <div *dxTemplate="let cell of 'currentCell'">
          <app-discount-value-cell
            [tier1Value]="cell.data.current_discount_percent"
            [tier1Name]="cell.data.discount_name"
            valueContext="Current"
            [precision]="1"
          ></app-discount-value-cell>
        </div>
        <!-- The supplier's REQUESTED discount (Story 3.3) — accent-highlighted, % only. -->
        <div *dxTemplate="let cell of 'requestedCell'">
          <span class="pf-requested">
            <app-discount-value-cell
              [tier1Value]="cell.data.requested_discount_percent"
              [tier1Name]="cell.data.discount_name"
              valueContext="Requested"
              [precision]="1"
            ></app-discount-value-cell>
          </span>
        </div>
        <!-- pp change CURRENT → REQUESTED (requested − current) — distinct from delta_pp (vs baseline).
             A breached discount renders in the alert colour (Story 3.6, AC3) — driven by the server-
             computed breached flag, never client-side threshold maths. -->
        <div *dxTemplate="let cell of 'requestedDeltaCell'">
          <app-discount-delta-cell
            [deltaPp]="cell.data.requested_delta_pp"
            [tierName]="cell.data.discount_name"
            [breached]="cell.data.breached"
          ></app-discount-delta-cell>
        </div>
        <div *dxTemplate="let cell of 'statusCell'">
          <app-status-badge [status]="cell.data.status"></app-status-badge>
          <app-outlier-badge *ngIf="cell.data.is_outlier"></app-outlier-badge>
        </div>

        <!-- Actions column: Confirm (green, Story 3.3) + Update (secondary counter, Story 3.4). -->
        <div *dxTemplate="let cell of 'actionsCell'">
          <div class="pf-actions">
            <dx-button
              text="Confirm"
              type="success"
              stylingMode="contained"
              [disabled]="confirmingIds.has(cell.data.id)"
              (onClick)="confirm(cell.data)"
              [attr.aria-label]="'Confirm the supplier update request for ' + (cell.data.product_name || cell.data.product_code)"
            ></dx-button>
            <dx-button
              text="Update"
              stylingMode="outlined"
              [disabled]="confirmingIds.has(cell.data.id)"
              (onClick)="openCounter(cell.data)"
              [attr.aria-label]="'Counter with revised discount for ' + (cell.data.product_name || cell.data.product_code)"
            ></dx-button>
          </div>
        </div>

        <!-- Inline counter form (master-detail) — one discount input per configured tier + a live
             "Supplier requested → Counter" helper, no $, no note (AC1/AC2). Rendered only for the row
             whose Update button opened it. -->
        <div *dxTemplate="let detail of 'counterForm'">
          <div class="pf-update-form" *ngIf="counterRow && counterRow.id === detail.data.id"
               (keydown.escape)="cancelCounter()">
            <div class="pf-update-fields">
              <div class="pf-update-field">
                <label class="pf-update-label">Updated discount (%)</label>
                <dx-number-box
                  [value]="$any(counterForm.value)"
                  [showSpinButtons]="false"
                  format="#0.##"
                  [inputAttr]="{ 'aria-label': 'Counter discount percent' }"
                  (onValueChanged)="onCounterValueChanged($event)"
                ></dx-number-box>
                <div class="pf-update-helper" [ngClass]="deltaClass(detail.data.requested_discount_percent, counterForm.value)" aria-live="polite">
                  {{ counterHelper(detail.data.requested_discount_percent, counterForm.value) }}
                </div>
                <div class="pf-field-error" role="alert" *ngIf="counterForm.error">{{ counterForm.error }}</div>
              </div>
            </div>
            <div class="pf-update-actions">
              <dx-button text="Send update" type="default" [disabled]="counterForm.submitting" (onClick)="submitCounter()"></dx-button>
              <dx-button text="Cancel" stylingMode="text" [disabled]="counterForm.submitting" (onClick)="cancelCounter()"></dx-button>
            </div>
          </div>
        </div>
      </dx-data-grid>
    </div>

    <!-- The Accepted date-filtered empty case (Story 4.6, AC4) is handled by the grid's own no-data text
         (gridNoDataText returns the date-range copy when acceptedDateEmpty) so the toolbar stays present. -->

    <!-- ---------- Accepted tab (§3e) ---------- -->
    <div
      *ngSwitchCase="'accepted'"
      [hidden]="currentState.loading || currentState.error"
      class="pf-table-wrap"
    >
      <dx-data-grid
        #acceptedGrid
        [dataSource]="displayRows.accepted"
        keyExpr="id"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [wordWrapEnabled]="true"
        height="100%"
        [noDataText]="gridNoDataText(currentTab)"
        (onRowExpanded)="onRowExpanded('accepted', $event)"
        (onRowCollapsed)="onRowCollapsed('accepted', $event)"
        (onContentReady)="onGridContentReady('accepted', $event)"
        aria-label="Accepted discount changes"
      >
        <!-- Search field in the grid toolbar — see Pending grid note. -->
        <dxo-search-panel [visible]="true" [width]="240" placeholder="Search products…"></dxo-search-panel>
        <!-- Native column header filters (funnel icons) — replace the retired Story 4.4 status chips +
             Story 4.5 Discount Delta toolbar controls. Wired on the Status + Change columns below. -->
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <!-- Supplier (4.2) / Group-by (4.3) / Delta (4.5) filters on the grid's own toolbar, beside the
             search box — outlet the shared templates; [visible] mirrors each control's original show rule. -->
        <dxo-toolbar>
          <dxi-item location="before" [visible]="showSupplierBar">
            <div *dxTemplate><ng-container [ngTemplateOutlet]="supplierFilterTpl"></ng-container></div>
          </dxi-item>
          <dxi-item location="before" [visible]="showGroupByBar">
            <div *dxTemplate><ng-container [ngTemplateOutlet]="groupByTpl"></ng-container></div>
          </dxi-item>
          <dxi-item name="searchPanel" location="after"></dxi-item>
        </dxo-toolbar>
        <dxo-grouping [autoExpandAll]="true"></dxo-grouping>
        <!-- No pager — virtual scrolling lets the user scroll the full result set in one list while
             keeping render fast at 3K+ rows (mirrors the supplier portal grids). -->
        <dxo-paging [pageSize]="50"></dxo-paging>
        <dxo-scrolling mode="virtual" rowRenderingMode="virtual"></dxo-scrolling>
        <dxi-column dataField="catalogue" caption="Catalogue" cssClass="pf-col-catalogue"
          [groupIndex]="catalogueGroupIndex" groupCellTemplate="groupHeader"></dxi-column>
        <dxi-column dataField="promotion_name" dataType="string" caption="Promotion" [minWidth]="180"
          [allowFiltering]="true" [allowHeaderFiltering]="true" [allowGrouping]="true"
          [showWhenGrouped]="true" [groupIndex]="promotionGroupIndex" groupCellTemplate="groupHeader"></dxi-column>

        <dxi-column caption="Product" dataField="product_name" cssClass="pf-col-product" cellTemplate="productCell"
          [allowSorting]="false" [calculateCellValue]="productSearchValue"></dxi-column>
        <dxi-column dataField="supplier_name" caption="Supplier" cssClass="pf-col-supplier" cellTemplate="supplierCell"
          [allowSorting]="false" [groupIndex]="supplierGroupIndex" groupCellTemplate="groupHeader"></dxi-column>
        <dxi-column caption="Previous (%)" cssClass="pf-col-discount" cellTemplate="previousCell" [allowSorting]="false"></dxi-column>
        <dxi-column caption="Previous ($)" dataType="number" cellTemplate="amountCell"
          [calculateCellValue]="previousDiscountAmount" [allowFiltering]="false"></dxi-column>
        <dxi-column caption="Confirmed (%)" cssClass="pf-col-discount" cellTemplate="confirmedCell" [allowSorting]="false"></dxi-column>
        <dxi-column caption="Confirmed ($)" dataType="number" cellTemplate="amountCell"
          [calculateCellValue]="currentDiscountAmount" [allowFiltering]="false"></dxi-column>
        <div *dxTemplate="let cell of 'amountCell'">
          {{ cell.value == null ? '—' : (cell.value | currency:'AUD':'symbol-narrow':'1.2-2') }}
        </div>
        <dxi-column caption="Change" cssClass="pf-col-delta" cellTemplate="deltaCell" [allowSorting]="false"
          [calculateCellValue]="deltaBandValue" [calculateFilterExpression]="deltaFilterExpression"
          [headerFilter]="deltaHeaderFilter" [allowSearch]="false"></dxi-column>
        <dxi-column caption="Accepted Date" cellTemplate="acceptedDateCell" [allowSorting]="false"></dxi-column>

        <!-- Shared group-header (§4.9, Story 4.3) — see Pending grid note. -->
        <div *dxTemplate="let group of 'groupHeader'" class="pf-group-header" [attr.aria-label]="groupAriaLabel(group)">
          <span class="pf-group-label">{{ groupHeaderLabel(group) }}</span>
          <span class="pf-group-summary" [ngClass]="groupAvgDeltaClass(group)">{{ groupAvgDeltaText(group) }}</span>
        </div>

        <div *dxTemplate="let cell of 'productCell'">
          <div class="pf-product-name">{{ cell.data.product_name || cell.data.product_code }}</div>
          <div class="pf-product-code">{{ cell.data.product_code }}</div>
        </div>
        <div *dxTemplate="let cell of 'supplierCell'">{{ cell.data.supplier_name }}</div>
        <div *dxTemplate="let cell of 'previousCell'">
          <app-discount-value-cell
            [tier1Value]="cell.data.previous_discount_percent"
            [tier1Name]="cell.data.discount_name"
            valueContext="Previous"
            [precision]="1"
          ></app-discount-value-cell>
        </div>
        <div *dxTemplate="let cell of 'confirmedCell'">
          <span class="pf-confirmed">
            <app-discount-value-cell
              [tier1Value]="cell.data.current_discount_percent"
              [tier1Name]="cell.data.discount_name"
              valueContext="Confirmed"
              [precision]="1"
            ></app-discount-value-cell>
          </span>
        </div>
        <div *dxTemplate="let cell of 'deltaCell'">
          <app-discount-delta-cell [deltaPp]="cell.data.delta_pp" [tierName]="cell.data.discount_name"></app-discount-delta-cell>
        </div>
        <div *dxTemplate="let cell of 'acceptedDateCell'">
          <span class="pf-product-code" *ngIf="cell.data.effective_date; else noAcceptedDate">{{ cell.data.effective_date | date:'d MMM y' }}</span>
          <ng-template #noAcceptedDate><span class="pf-product-code">—</span></ng-template>
        </div>
      </dx-data-grid>
    </div>

  </ng-container>

  <!-- ============================ Bulk Accept confirmation modal (§3a, BR-12) ============================ -->
  <dx-popup
    [(visible)]="bulkAcceptVisible"
    [width]="500"
    height="auto"
    [showTitle]="true"
    title="Confirm bulk accept"
    [dragEnabled]="false"
    [hideOnOutsideClick]="true"
  >
    <div *dxTemplate="let data of 'content'">
      <div class="pf-modal-body">
        <p>
          Accept <strong>{{ bulkProductsCount }}</strong> product(s) across
          <strong>{{ bulkSupplierCount }}</strong> supplier(s)?
        </p>
        <p class="pf-modal-line"><span class="pf-modal-key">Catalogues:</span> {{ bulkCatalogueBreakdown }}</p>
        <p class="pf-modal-line">
          <span class="pf-modal-key">Average change:</span>
          <span [ngClass]="bulkAvgDeltaClass">{{ bulkAvgDeltaText }}</span>
        </p>
        <p class="pf-modal-warning" *ngIf="bulkHasOutlier" role="alert">
          One or more selected products are flagged as outliers — review before accepting.
        </p>
        <p class="pf-modal-note">
          Accepting confirms each product's current discount(s) and sets the effective date. This can't be undone.
        </p>
      </div>
      <div class="pf-modal-actions">
        <dx-button text="Cancel" stylingMode="text" [disabled]="bulkAcceptSubmitting" (onClick)="bulkAcceptVisible = false"></dx-button>
        <dx-button text="Confirm" type="success" [disabled]="bulkAcceptSubmitting" (onClick)="confirmBulkAccept()"></dx-button>
      </div>
    </div>
  </dx-popup>

  <!-- ============================ Bulk Update modal (§3c) ============================ -->
  <dx-popup
    [(visible)]="bulkUpdateVisible"
    [width]="500"
    height="auto"
    [showTitle]="true"
    title="Bulk update discounts"
    [dragEnabled]="false"
    [hideOnOutsideClick]="true"
  >
    <div *dxTemplate="let data of 'content'">
      <div class="pf-modal-body">
        <p>Apply a revised discount to <strong>{{ selectedCount }}</strong> selected product(s).</p>
        <div class="pf-update-fields">
          <div class="pf-update-field">
            <label class="pf-update-label">Updated discount (%)</label>
            <dx-number-box
              [value]="$any(bulkUpdateForm.value)"
              [showSpinButtons]="false"
              format="#0.##"
              [inputAttr]="{ 'aria-label': 'Updated discount percent' }"
              (onValueChanged)="onBulkUpdateValueChanged($event)"
            ></dx-number-box>
            <div class="pf-field-error" role="alert" *ngIf="bulkUpdateForm.error">{{ bulkUpdateForm.error }}</div>
          </div>
        </div>
        <p class="pf-modal-note">The revised discount is sent back to the supplier; the products stay in Pending Review.</p>
      </div>
      <div class="pf-modal-actions">
        <dx-button text="Cancel" stylingMode="text" [disabled]="bulkUpdateForm.submitting" (onClick)="bulkUpdateVisible = false"></dx-button>
        <dx-button text="Send update" type="default" [disabled]="bulkUpdateForm.submitting" (onClick)="submitBulkUpdate()"></dx-button>
      </div>
    </div>
  </dx-popup>
</section>
